import { AccommodationPlaceRoomBlockStatus, Maybe } from '@graphql/generated';
import { AccommodationCombinedType, AccommodationPlacesV2 } from './Accommodation.types';
import { isNull } from 'lodash-es';
import { distanceInMiles } from '@shared/utils/distanceInMiles';

export const accommodationPlacesV2ToAccommodationCombined = (
  accommodationPlacesV2: AccommodationPlacesV2,
  origin: { latitude?: number | null; longitude?: number | null } | null | undefined,
) => {
  const isRoomBlock = !isNull(accommodationPlacesV2?.accommodationRoomBlock) && accommodationPlacesV2?.accommodationRoomBlock?.status === AccommodationPlaceRoomBlockStatus.active;
  const latitude = accommodationPlacesV2?.lat;
  const longitude = accommodationPlacesV2?.long;
  let distance: number | null = null;
  distance = distanceInMiles(origin, { lat: latitude, lng: longitude });

  let accommodationType: AccommodationCombinedType;
  if (isRoomBlock) {
    accommodationType = AccommodationCombinedType.Roomblock;
  } else if (accommodationPlacesV2.isCustomLinkEnabled) {
    accommodationType = AccommodationCombinedType.Custom;
  } else {
    accommodationType = AccommodationCombinedType.Hotel;
  }

  return {
    id: accommodationPlacesV2.id,
    type: accommodationType,
    accommodationType: accommodationPlacesV2.type,

    //   General Information
    displayName: accommodationPlacesV2?.customName || accommodationPlacesV2?.name,
    address: accommodationPlacesV2?.customAddress || accommodationPlacesV2?.address,
    customAddress: accommodationPlacesV2?.customAddress,
    city: accommodationPlacesV2?.city,
    country: accommodationPlacesV2?.country,
    state: accommodationPlacesV2?.state,
    postalCode: accommodationPlacesV2?.postalCode,
    latitude: accommodationPlacesV2?.lat,
    longitude: accommodationPlacesV2?.long,
    distance: distance,
    distances: accommodationPlacesV2.distances,

    description: accommodationPlacesV2.userNote,
    email: accommodationPlacesV2?.email,
    phone: accommodationPlacesV2?.customPhone || accommodationPlacesV2?.phone,
    url: accommodationPlacesV2?.customUrl || accommodationPlacesV2?.url,
    customUrl: accommodationPlacesV2?.customUrl,

    // JoyPlaceID
    joyPlaceId: accommodationPlacesV2?.joyPlaceId,

    //   Provider Information
    googlePlaceId: accommodationPlacesV2?.googlePlaceId,
    zhId: accommodationPlacesV2?.zhId,

    //   Extra Information
    pricePerNight: isRoomBlock ? accommodationPlacesV2.accommodationRoomBlock?.pricePerNight : undefined,
    bookingUrl: isRoomBlock ? accommodationPlacesV2.accommodationRoomBlock?.bookingUrl : undefined,
    cutoffDate: isRoomBlock ? accommodationPlacesV2.accommodationRoomBlock?.cutoffDate : undefined,
    checkOutDate: isRoomBlock ? accommodationPlacesV2.accommodationRoomBlock?.checkOutDate : undefined,
    checkInDate: isRoomBlock ? accommodationPlacesV2.accommodationRoomBlock?.checkInDate : undefined,
    strikeoutPricePerNight: isRoomBlock ? accommodationPlacesV2.accommodationRoomBlock?.strikeoutPricePerNight : undefined,
    numberOfRoomsReserved: isRoomBlock ? accommodationPlacesV2.accommodationRoomBlock?.numberOfRoomsReserved : undefined,
    isJoyManagedRoomBlock: isRoomBlock ? accommodationPlacesV2.accommodationRoomBlock?.isJoyManagedRoomBlock : undefined,

    //   Custom Properties
    isFavorite: accommodationPlacesV2?.isFavorite,
    starRating: accommodationPlacesV2?.starRating,

    photo: accommodationPlacesV2.isPhotoHidden ? null : accommodationPlacesV2.customPhoto || accommodationPlacesV2.photo,
    isFromAccommodationCatalog: accommodationPlacesV2.isFromAccommodationCatalog,
    isCustomLinkEnabled: accommodationPlacesV2.isCustomLinkEnabled,
  };
};

/**
 * Determines if an accommodation place should display a link.
 * Returns false only if isCustomLinkEnabled is true and customUrl is missing; otherwise returns true.
 *
 * @param isCustomLinkEnabled - Whether the custom link feature is enabled for this accommodation.
 * @param customUrl - The custom URL for the accommodation, if any.
 * @returns True if the accommodation should show a link, false otherwise.
 */
export const hasAccommodationLink = (isCustomLinkEnabled?: Maybe<boolean>, customUrl?: Maybe<string>): boolean => {
  // Only return true if there's a custom URL and isCustomLinkEnabled is enabled
  if (isCustomLinkEnabled) return Boolean(customUrl);

  // Otherwise, got to customUrl or url or google map url
  return true;
};
